import { useEffect, useMemo, useState } from "react";
import { BarChart3, Download, Eye, Layers, RefreshCw } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  fetchAdminWebsiteRecommendations,
  fetchWebsiteRecommendations,
  type RecommendationItem,
  type RecommendationMethodGroup,
  type SimilarRankingMethod,
  type SimilarRecommendationPayload,
} from "@/services/websiteDetail.service";

type WebsiteRecommendationWorkbenchProps = {
  websiteId: string;
};

const METHOD_ORDER: SimilarRankingMethod[] = [
  "METADATA_BASELINE",
  "BM25_TEXT",
  "SEMANTIC_EMBEDDING",
  "HYBRID_RRF",
];

const METHOD_LABELS: Record<SimilarRankingMethod, string> = {
  METADATA_BASELINE: "Metadata",
  BM25_TEXT: "BM25",
  SEMANTIC_EMBEDDING: "Semantic",
  HYBRID_RRF: "Hybrid",
};

const scoreLabel = (score?: number | null) =>
  typeof score === "number" && Number.isFinite(score) ? score.toFixed(4) : "-";

const mutedTextStyle = {
  color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
} as const;

const bodyTextStyle = {
  color: "color-mix(in srgb, var(--foreground) 82%, var(--surface))",
} as const;

const panelClassName = "min-w-0 rounded-md border bg-card/60 p-3";

export function WebsiteRecommendationWorkbench({ websiteId }: WebsiteRecommendationWorkbenchProps) {
  const { t } = useApp();
  const [adminPayload, setAdminPayload] = useState<SimilarRecommendationPayload | null>(null);
  const [publicPayload, setPublicPayload] = useState<SimilarRecommendationPayload | null>(null);
  const [activeMethod, setActiveMethod] = useState<SimilarRankingMethod>("METADATA_BASELINE");
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    setAdminPayload(null);
    setPublicPayload(null);

    Promise.all([
      fetchAdminWebsiteRecommendations(websiteId, { signal: controller.signal }),
      fetchWebsiteRecommendations(websiteId, { signal: controller.signal }),
    ])
      .then(([adminData, publicData]) => {
        setAdminPayload(adminData);
        setPublicPayload(publicData);
        const firstAvailable = adminData.methodGroups.find((group) => group.available)?.method;
        setActiveMethod(firstAvailable ?? "METADATA_BASELINE");
      })
      .catch((error) => {
        if (error?.code !== "ERR_CANCELED") {
          setFailed(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [websiteId]);

  const groups = adminPayload?.methodGroups ?? [];
  const groupByMethod = useMemo(() => {
    const map = new Map<SimilarRankingMethod, RecommendationMethodGroup>();
    for (const group of groups) {
      map.set(group.method, group);
    }
    return map;
  }, [groups]);
  const activeGroup = groupByMethod.get(activeMethod) ?? groups[0];
  const publicGroup = publicPayload?.methodGroups?.[0];
  const run = adminPayload?.metadata;

  const downloadAdminJson = () => {
    if (!adminPayload) return;
    const blob = new Blob([JSON.stringify(adminPayload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `webkurator-recommendations-${websiteId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderRecommendationItem = (item: RecommendationItem) => (
    <div key={`${item.stableId ?? item.id}-${item.rank}`} className="rounded-md border bg-background/40 p-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="break-words text-sm font-medium" style={bodyTextStyle}>
            #{item.rank} {item.title || item.hostname || item.id}
          </div>
          {item.hostname && (
            <div className="mt-0.5 break-all text-xs" style={mutedTextStyle}>
              {item.hostname}
            </div>
          )}
        </div>
        <Badge variant="outline" className="shrink-0 text-[11px] tabular-nums">
          {scoreLabel(item.score)}
        </Badge>
      </div>
      {item.evidence && (
        <div
          className="mt-2 text-xs leading-relaxed"
          style={{
            ...mutedTextStyle,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.evidence}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className={panelClassName}>
        <div className="space-y-3">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  if (failed) {
    return (
      <div className="rounded-md border p-3 text-sm text-destructive">
        {t("websiteDetails.researchComparisonUnavailable")}
      </div>
    );
  }

  if (!adminPayload) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold" style={bodyTextStyle}>
            <BarChart3 className="h-4 w-4" />
            {t("websiteDetails.researchComparisonTitle")}
          </div>
          <div className="text-xs" style={mutedTextStyle}>
            {t("websiteDetails.researchComparisonSubtitle")}
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={downloadAdminJson}>
          <Download className="h-4 w-4" />
          {t("websiteDetails.exportJson")}
        </Button>
      </div>

      <div className={panelClassName}>
        <div className="mb-3 flex items-center gap-2 text-sm font-medium" style={bodyTextStyle}>
          <Layers className="h-4 w-4" />
          {t("websiteDetails.methodRankings")}
        </div>
        <div className="mb-3 grid min-w-0 grid-cols-2 gap-1 sm:grid-cols-4">
          {METHOD_ORDER.map((method) => {
            const group = groupByMethod.get(method);
            const selected = method === activeMethod;
            return (
              <Button
                key={method}
                type="button"
                variant={selected ? "default" : "outline"}
                size="sm"
                className="h-8 min-w-0 gap-1.5 px-2 text-xs"
                disabled={!group}
                onClick={() => setActiveMethod(method)}
                title={group?.note ?? METHOD_LABELS[method]}
              >
                <RefreshCw className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 truncate">{METHOD_LABELS[method]}</span>
              </Button>
            );
          })}
        </div>

        {activeGroup ? (
          <div className="space-y-2">
            {activeGroup.note && (
              <div className="text-xs" style={mutedTextStyle}>
                {activeGroup.note}
              </div>
            )}
            <div className="space-y-2">
              {activeGroup.items.slice(0, 5).map(renderRecommendationItem)}
            </div>
          </div>
        ) : (
          <div className="text-sm" style={mutedTextStyle}>
            {t("websiteDetails.noResearchItems")}
          </div>
        )}
      </div>

      <div className={panelClassName}>
        <div className="mb-3 flex items-center gap-2 text-sm font-medium" style={bodyTextStyle}>
          <Eye className="h-4 w-4" />
          {t("websiteDetails.publicPreview")}
        </div>
        {(publicGroup?.items ?? []).length > 0 ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {(publicGroup?.items ?? []).slice(0, 5).map((item) => (
              <div key={item.stableId ?? item.id} className="rounded-md border bg-background/40 p-2.5">
                <div className="break-words text-sm font-medium" style={bodyTextStyle}>
                  {item.title || item.hostname || item.id}
                </div>
                {item.hostname && (
                  <div className="mt-0.5 break-all text-xs" style={mutedTextStyle}>
                    {item.hostname}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm" style={mutedTextStyle}>
            {t("websiteDetails.noPublicPreview")}
          </div>
        )}
      </div>

      <div className={panelClassName}>
        <div className="mb-2 text-sm font-medium" style={bodyTextStyle}>
          {t("websiteDetails.comparisonTable")}
        </div>
        <div className="-mx-1 overflow-x-auto px-1">
          <Table className="min-w-[520px]">
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[180px] text-xs">{t("websiteDetails.websiteColumn")}</TableHead>
                <TableHead className="text-xs">{t("websiteDetails.bestRank")}</TableHead>
                <TableHead className="text-xs">{t("websiteDetails.rankSpread")}</TableHead>
                {METHOD_ORDER.map((method) => (
                  <TableHead key={method} className="text-xs">{METHOD_LABELS[method]}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {adminPayload.candidateComparisons.slice(0, 20).map((item) => (
                <TableRow key={item.stableId ?? item.id}>
                  <TableCell className="max-w-[220px] whitespace-normal py-2 text-xs">
                    <div className="font-medium">{item.title || item.hostname || item.id}</div>
                    {item.hostname && (
                      <div className="break-all" style={mutedTextStyle}>
                        {item.hostname}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-xs">{item.bestRank ?? "-"}</TableCell>
                  <TableCell className="py-2 text-xs">{item.rankSpread ?? "-"}</TableCell>
                  {METHOD_ORDER.map((method) => (
                    <TableCell key={method} className="py-2 text-xs">
                      {item.ranksByMethod?.[method] ?? "-"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="grid min-w-0 gap-2 rounded-md border bg-card/60 p-3 text-xs [overflow-wrap:anywhere]" style={mutedTextStyle}>
        <div className="min-w-0 break-all">{t("websiteDetails.dataset")}: {run?.datasetSha256 || t("websiteDetails.notConfigured")}</div>
        <div className="min-w-0 break-all">{t("websiteDetails.embeddingModel")}: {run?.embeddingModelId || t("websiteDetails.notConfigured")}</div>
        <div className="min-w-0 break-words">{t("websiteDetails.textFields")}: {run?.textFields || "title + description + keywords + main.main_content"}</div>
        <div className="min-w-0 break-all">{t("websiteDetails.hybrid")}: {run?.hybridRule || run?.hybridConfig || "RRF(k=60)"}</div>
      </div>
    </div>
  );
}
